/*  ═══════════════════════════════════════════════════════════
    Paint Colour Matcher — Eizi Design Co.
    eizidesignco.au

    Matches HEX, RGB or PMS input to closest Australian
    paint colours using CIEDE2000 perceptual colour difference.

    LEGAL: Colour values are physical measurements (facts).
    Brand names shown for reference only with attribution.
    All trademarks belong to their respective owners.
    ═══════════════════════════════════════════════════════════ */

(function () {
  "use strict";

  var DB = [];
  var PMS = null;
  var ready = false;

  var BRANDS = {
    d:  { name: "Dulux\u00AE Australia",  site: "dulux.com.au" },
    t:  { name: "Taubmans\u00AE",         site: "taubmans.com.au" },
    bp: { name: "British Paints\u00AE",    site: "britishpaints.com.au" },
    pp: { name: "Porter\u2019s Paints",    site: "porterspaints.com" }
  };

  function dn(s) { try { return atob(s); } catch(e) { return s; } }

  function lin(c) {
    c /= 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  function rgbToLab(r, g, b) {
    var rl = lin(r), gl = lin(g), bl = lin(b);
    var x = (0.4124564*rl + 0.3575761*gl + 0.1804375*bl) / 0.95047;
    var y = (0.2126729*rl + 0.7151522*gl + 0.0721750*bl) / 1.00000;
    var z = (0.0193339*rl + 0.1191920*gl + 0.9503041*bl) / 1.08883;
    var f = function(t) { return t > 0.008856 ? Math.cbrt(t) : (903.3*t+16)/116; };
    var fx=f(x), fy=f(y), fz=f(z);
    return [116*fy-16, 500*(fx-fy), 200*(fy-fz)];
  }

  function dE00(lab1, lab2) {
    var L1=lab1[0],a1=lab1[1],b1=lab1[2],L2=lab2[0],a2=lab2[1],b2=lab2[2];
    var R=Math.PI/180,D=180/Math.PI;
    var aL=(L1+L2)/2,C1=Math.sqrt(a1*a1+b1*b1),C2=Math.sqrt(a2*a2+b2*b2);
    var aC=(C1+C2)/2;
    var G=0.5*(1-Math.sqrt(Math.pow(aC,7)/(Math.pow(aC,7)+Math.pow(25,7))));
    var a1p=a1*(1+G),a2p=a2*(1+G);
    var C1p=Math.sqrt(a1p*a1p+b1*b1),C2p=Math.sqrt(a2p*a2p+b2*b2);
    var aCp=(C1p+C2p)/2;
    var h1p=(Math.atan2(b1,a1p)*D+360)%360,h2p=(Math.atan2(b2,a2p)*D+360)%360;
    var dHp;
    if(C1p*C2p===0)dHp=0;
    else if(Math.abs(h2p-h1p)<=180)dHp=h2p-h1p;
    else dHp=h2p-h1p>180?h2p-h1p-360:h2p-h1p+360;
    var dLp=L2-L1,dCp=C2p-C1p;
    var dHpT=2*Math.sqrt(C1p*C2p)*Math.sin(dHp/2*R);
    var aHp;
    if(C1p*C2p===0)aHp=h1p+h2p;
    else if(Math.abs(h1p-h2p)<=180)aHp=(h1p+h2p)/2;
    else aHp=(h1p+h2p+(h1p+h2p<360?360:0))/2;
    var T=1-0.17*Math.cos((aHp-30)*R)+0.24*Math.cos(2*aHp*R)+0.32*Math.cos((3*aHp+6)*R)-0.20*Math.cos((4*aHp-63)*R);
    var SL=1+0.015*Math.pow(aL-50,2)/Math.sqrt(20+Math.pow(aL-50,2));
    var SC=1+0.045*aCp,SH=1+0.015*aCp*T;
    var dT=30*Math.exp(-Math.pow((aHp-275)/25,2));
    var RC=2*Math.sqrt(Math.pow(aCp,7)/(Math.pow(aCp,7)+Math.pow(25,7)));
    var RT=-RC*Math.sin(2*dT*R);
    return Math.sqrt(Math.pow(dLp/SL,2)+Math.pow(dCp/SC,2)+Math.pow(dHpT/SH,2)+RT*(dCp/SC)*(dHpT/SH));
  }

  function conf(de) {
    if(de<1)   return {t:"Near-perfect match",   c:"cm-c1",s:"\u2605\u2605\u2605\u2605\u2605"};
    if(de<2)   return {t:"Excellent match",       c:"cm-c1",s:"\u2605\u2605\u2605\u2605\u2605"};
    if(de<3.5) return {t:"Good match",            c:"cm-c2",s:"\u2605\u2605\u2605\u2605\u2606"};
    if(de<5)   return {t:"Approximate match",     c:"cm-c3",s:"\u2605\u2605\u2605\u2606\u2606"};
    if(de<10)  return {t:"Closest available",      c:"cm-c4",s:"\u2605\u2605\u2606\u2606\u2606"};
    return      {t:"No close match found",    c:"cm-c5",s:"\u2605\u2606\u2606\u2606\u2606"};
  }

  function findTop(lab, brand, n) {
    n = n || 3;
    var res = [];
    for (var i = 0; i < DB.length; i++) {
      if (DB[i].b !== brand) continue;
      res.push({ idx:i, dE: dE00(lab, DB[i].l) });
    }
    res.sort(function(a,b){ return a.dE - b.dE; });
    return res.slice(0, n).map(function(r){
      var c = DB[r.idx];
      return { name: dn(c.n), hex:"#"+c.h, url:c.u, dE:r.dE };
    });
  }

  function parseHex(s) {
    s = s.replace(/^#/, "").trim();
    if (/^[0-9a-f]{6}$/i.test(s))
      return [parseInt(s.slice(0,2),16),parseInt(s.slice(2,4),16),parseInt(s.slice(4,6),16)];
    return null;
  }

  function parseRgb(s) {
    var m = s.match(/(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})/);
    if (m) { var r=+m[1],g=+m[2],b=+m[3]; if(r<=255&&g<=255&&b<=255) return [r,g,b]; }
    return null;
  }

  function parsePms(s) {
    if (!PMS) return null;
    var k = s.toUpperCase().replace(/^(PANTONE|PMS)\s*/i,"").replace(/\s+/g," ").trim();
    var hex = PMS[k] || PMS[k.replace(/\s+([CU])$/,"$1")] || PMS[k.replace(/([CU])$/," $1")];
    return hex ? parseHex(hex) : null;
  }

  function resolve(raw) {
    raw = raw.trim();
    if (!raw) return null;
    var rgb = parseHex(raw);
    if (rgb) return { rgb:rgb, label:"HEX #"+raw.replace("#","").toUpperCase() };
    rgb = parseRgb(raw);
    if (rgb) return { rgb:rgb, label:"RGB("+rgb.join(", ")+")" };
    rgb = parsePms(raw);
    if (rgb) return { rgb:rgb, label:"PMS "+raw.replace(/^(PANTONE|PMS)\s*/i,"").toUpperCase() };
    return null;
  }

  function toHex(rgb) {
    return "#"+rgb.map(function(v){return v.toString(16).padStart(2,"0");}).join("");
  }

  function render(container, rgb, label, results) {
    var ih = toHex(rgb);
    var h = '<div class="cm-preview"><div class="cm-sw-lg" style="background:'+ih+'"></div>'
      +'<div class="cm-pi"><span class="cm-pl">Your colour</span>'
      +'<span class="cm-pv">'+label+'</span>'
      +'<span class="cm-ph">'+ih+' \u00B7 RGB('+rgb.join(", ")+')</span></div></div>'
      +'<div class="cm-brands">';

    var order = ["d","t","bp","pp"];
    for (var bi=0; bi<order.length; bi++) {
      var brand = order[bi];
      var matches = results[brand];
      if (!matches || !matches.length) continue;
      var br = BRANDS[brand];

      h += '<div class="cm-bs"><h3 class="cm-bt">'+br.name+'</h3><div class="cm-ms">';
      for (var i=0; i<matches.length; i++) {
        var m = matches[i], cf = conf(m.dE);
        h += '<div class="cm-m'+(i===0?' cm-m1':'')+'">'
          +'<div class="cm-ms-sw" style="background:'+m.hex+'"></div>'
          +'<div class="cm-mi">'
          +'<div class="cm-mn">'+m.name+'</div>'
          +'<div class="cm-mh">'+m.hex+'</div>'
          +'<div class="cm-mc '+cf.c+'"><span class="cm-st">'+cf.s+'</span> '+cf.t+'</div>'
          +'<div class="cm-md">\u0394E '+m.dE.toFixed(1)+'</div>'
          +(m.url?'<a class="cm-ml" href="'+m.url+'" target="_blank" rel="noopener noreferrer">View on '+br.site+' \u2192</a>':'')
          +'</div></div>';
      }
      h += '</div></div>';
    }

    h += '</div><div class="cm-cta">'
      +'<p class="cm-cta-t">Need this exact colour on a custom prop, plinth, or backdrop?</p>'
      +'<a href="/pages/contact" class="cm-cta-b">Get a Quote \u2192</a>'
      +'<p class="cm-disc">Digital colour approximations only. Always confirm with a physical paint swatch before purchasing.</p>'
      +'</div>';

    container.innerHTML = h;
    container.style.display = "block";
  }

  function $(id) { return document.getElementById(id); }

  async function init() {
    var status = $("cm-status");
    try {
      status.textContent = "Loading colour database\u2026";
      var resp = await fetch($("cm-data-url").value);
      DB = await resp.json();
      if (window.__PMS_DATA) PMS = window.__PMS_DATA;
      ready = true;
      status.textContent = DB.length.toLocaleString() + " paint colours loaded";
      status.className = "cm-status cm-ok";
      $("cm-input").disabled = false;
      $("cm-go").disabled = false;
    } catch(e) {
      status.textContent = "Error loading data. Please refresh the page.";
      status.className = "cm-status cm-err";
    }
  }

  function go() {
    var raw = $("cm-input").value;
    var err = $("cm-error"), res = $("cm-results");
    err.style.display = "none"; res.style.display = "none";
    if (!ready) { err.textContent = "Still loading \u2014 please wait a moment."; err.style.display = "block"; return; }
    var parsed = resolve(raw);
    if (!parsed) {
      err.textContent = 'Enter a HEX code (e.g. #9996DB), RGB values (e.g. 153, 150, 219), or a PMS code (e.g. 2715 C).';
      err.style.display = "block"; return;
    }
    var lab = rgbToLab(parsed.rgb[0], parsed.rgb[1], parsed.rgb[2]);
    var results = { d:findTop(lab,"d"), t:findTop(lab,"t"), bp:findTop(lab,"bp"), pp:findTop(lab,"pp") };
    render(res, parsed.rgb, parsed.label, results);
    res.scrollIntoView({ behavior:"smooth", block:"start" });
  }

  document.addEventListener("DOMContentLoaded", function(){
    init();
    $("cm-go").addEventListener("click", go);
    $("cm-input").addEventListener("keydown", function(e){ if(e.key==="Enter"){e.preventDefault();go();} });
  });

})();
